import { gql } from "@apollo/client";
import { client } from "../apolloClient";
import { useQuery, useMutation } from "@apollo/client";

/**
 * useCityApi custom hook for city API operations.
 * - Provides CRUD operations for cities using Apollo Client and GraphQL.
 * - Handles cache updates for city queries and mutations.
 * - Exposes helpers for reading/writing/updating cities in Apollo cache.
 * - Returns methods for fetching, creating, updating, and deleting cities.
 * - Ensures UI stays in sync with backend by updating Apollo cache after mutations.
 */

// GraphQL Queries and Mutations
const CITIES_QUERIES = {
  // Query to fetch all cities
  GET_CITIES: gql`
    query GetCities {
      GetCities {
        city_id
        country_id
        city_name
        is_active
        created_on
        updated_on
      }
    }
  `,

  // Query to fetch cities by country ID
  GET_CITIES_BY_COUNTRY_ID: gql`
    query GetCitiesByCountryId($countryId: ID!) {
      GetCitiesByCountryId(country_id: $countryId) {
        city_id
        city_name
        country_id
        is_active
        created_on
        updated_on
      }
    }
  `,

  // Mutation to add a new city
  ADD_CITY: gql`
    mutation AddCity($countryId: Int!, $cityName: String!) {
      AddCity(country_id: $countryId, city_name: $cityName) {
        city_id
        country_id
        city_name
        is_active
        created_on
        updated_on
      }
    }
  `,

  // Mutation to update an existing city
  UPDATE_CITY: gql`
    mutation UpdateCity($cityId: ID!, $cityName: String, $countryId: Int) {
      UpdateCity(
        city_id: $cityId
        city_name: $cityName
        country_id: $countryId
      ) {
        city_id
        country_id
        city_name
        is_active
        created_on
        updated_on
      }
    }
  `,

  // Mutation to delete a city
  DELETE_CITY: gql`
    mutation DeleteCity($cityId: ID!) {
      DeleteCity(city_id: $cityId) {
        city_id
        country_id
        city_name
        is_active
        created_on
        updated_on
      }
    }
  `,
};

// Cache operations for cities
const cacheOperations = {
  /**
   * Writes the provided cities array to Apollo Client cache.
   * @param {Array} cities - Array of city objects.
   */
  writeCities: (cities) => {
    if (!cities) return;
    client.writeQuery({
      query: CITIES_QUERIES.GET_CITIES,
      data: {
        GetCities: cities,
      },
    });
  },

  /**
   * Reads the cities array from Apollo Client cache.
   * @returns {Array} Array of city objects or empty array if not found.
   */
  readCities: () => {
    try {
      const { GetCities } =
        client.readQuery({
          query: CITIES_QUERIES.GET_CITIES,
        }) || {};
      return GetCities || [];
    } catch {
      return [];
    }
  },

  /**
   * Updates a city in the Apollo Client cache.
   * @param {Object} updatedCity - The updated city object.
   */
  updateCitiesInCache: (updatedCity) => {
    const existingCities = cacheOperations.readCities();
    const updatedCities = existingCities.map((city) =>
      city.city_id === updatedCity.city_id ? updatedCity : city
    );
    cacheOperations.writeCities(updatedCities);
  },
};

export const useCityApi = () => {
  const [addCityMutation] = useMutation(CITIES_QUERIES.ADD_CITY);
  const [updateCityMutation] = useMutation(CITIES_QUERIES.UPDATE_CITY);

  /**
   * Fetches all cities from the backend.
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const getCities = async () => {
    try {
      const { data } = await client.query({
        query: CITIES_QUERIES.GET_CITIES,
        fetchPolicy: "network-only",
      });
      return { success: true, data: data.GetCities };
    } catch (error) {
      console.error("Error fetching cities:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Fetches a city by its ID.
   * @param {number|string} city_id
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const getCityById = async (city_id) => {
    try {
      const { data } = await client.query({
        query: CITIES_QUERIES.GET_CITY_BY_ID,
        variables: { city_id },
        fetchPolicy: "network-only",
      });
      return { success: true, data: data.GetCityById };
    } catch (error) {
      console.error("Error fetching city:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Fetches all cities for a given country ID.
   * @param {number|string} country_id
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const getCitiesByCountryId = async (country_id) => {
    try {
      const { data } = await client.query({
        query: CITIES_QUERIES.GET_CITIES_BY_COUNTRY_ID,
        variables: { countryId: country_id },
        fetchPolicy: "network-only",
      });
      return { success: true, data: data.GetCitiesByCountryId };
    } catch (error) {
      console.error("Error fetching client POCs:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Creates a new city.
   * @param {Object} formData - { city_name, country_id, is_active }
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const createCity = async (formData) => {
    try {
      const { data, errors } = await addCityMutation({
        variables: {
          cityName: formData.city_name,
          countryId: parseInt(formData.country_id),
          isActive: formData.is_active,
        },
      });

      if (errors?.length > 0) {
        throw new Error(errors[0].message);
      }

      if (!data?.AddCity) {
        throw new Error("Failed to create city");
      }

      return {
        success: true,
        data: data.AddCity,
      };
    } catch (error) {
      // Handle GraphQL errors
      if (error.graphQLErrors?.length > 0) {
        const graphQLError = error.graphQLErrors[0];
        throw new Error(graphQLError.message);
      }

      // Handle network errors
      if (error.networkError?.result?.errors?.length > 0) {
        const networkError = error.networkError.result.errors[0];
        throw new Error(networkError.message);
      }

      throw error;
    }
  };

  /**
   * Updates an existing city.
   * @param {number|string} cityId
   * @param {Object} formData - { city_name, country_id, is_active }
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const updateCity = async (cityId, formData) => {
    try {
      const { data, errors } = await updateCityMutation({
        variables: {
          cityId: parseInt(cityId),
          cityName: formData.city_name,
          countryId: parseInt(formData.country_id),
          isActive: formData.is_active,
        },
      });

      if (errors?.length > 0) {
        throw new Error(errors[0].message);
      }

      if (!data?.UpdateCity) {
        throw new Error("Failed to update city");
      }

      return {
        success: true,
        data: data.UpdateCity,
      };
    } catch (error) {
      // Handle GraphQL errors
      if (error.graphQLErrors?.length > 0) {
        const graphQLError = error.graphQLErrors[0];
        throw new Error(graphQLError.message);
      }

      // Handle network errors
      if (error.networkError?.result?.errors?.length > 0) {
        const networkError = error.networkError.result.errors[0];
        throw new Error(networkError.message);
      }

      throw error;
    }
  };

  /**
   * Deletes a city by its ID.
   * @param {number|string} city_id
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const deleteCity = async (city_id) => {
    try {
      const { data } = await client.mutate({
        mutation: CITIES_QUERIES.DELETE_CITY,
        variables: { cityId: city_id },
      });

      const existingCities = cacheOperations.readCities();
      cacheOperations.writeCities(
        existingCities.filter((city) => city.city_id !== city_id)
      );

      return { success: true, data: data.DeleteClient };
    } catch (error) {
      console.error("Error deleting city:", error);
      return { success: false, error: error.message };
    }
  };

  return {
    getCities,
    getCityById,
    createCity,
    updateCity,
    deleteCity,
    getCitiesByCountryId,
    cacheOperations,
  };
};
